import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatEnumLabel } from '../../utils/format';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { VotingRulesCard } from './VotingRulesCard';
import { SORT_OPTIONS, politicsSortAccessor } from './StandingsTable';
import type { SortKey } from './StandingsTable';
import type { PoliticsShellProps } from '../../pages/Politics';

/**
 * SF-152: card-based mobile content for Politics. Standings render as a single bordered row-list
 * (mirrors MobileKingdomViewerShell's roster treatment, SF-169) rather than a horizontally-scrolled
 * table — a 6-column table doesn't fit a phone, and each row needs an inline action button.
 */
export function MobilePoliticsShell({ standings, myProvinceId, isVoting, handleVote, handleRetract }: PoliticsShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const [sortKey, setSortKey] = useState<SortKey>('votesReceived');

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const sortedStandings = useMemo(() => {
    const option = SORT_OPTIONS.find((opt) => opt.key === sortKey) ?? SORT_OPTIONS[0];
    return [...standings.standings].sort((a, b) => {
      const av = politicsSortAccessor(a, sortKey);
      const bv = politicsSortAccessor(b, sortKey);
      if (av < bv) return option.dir === 'asc' ? -1 : 1;
      if (av > bv) return option.dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [standings.standings, sortKey]);

  return (
    <div className="space-y-3">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Politics
      </h1>

      <VotingRulesCard leadRequirement={standings.leadRequirement} variant="card" />

      <GlanceCard title="Monarch">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-game-400">Monarch</span>
          <div className="flex-1 min-w-0 text-right">
            <span
              className="truncate min-w-0"
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                color: standings.monarchProvinceId ? COLORS.gold : COLORS.text.muted,
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {standings.monarchProvinceName ?? 'No Monarch'}
              {standings.monarchProvinceId && <span style={{ marginLeft: 4 }}>♛</span>}
            </span>
          </div>
        </div>

        <MetricRow label="Votes Cast" value={standings.totalVotesCast} unit="votes" weight="secondary" />
        <div className="text-[11px] -mt-1.5" style={{ color: COLORS.text.tertiary }}>
          of {standings.totalPossibleVotes} possible
        </div>

        <MetricRow label="To Crown" value={standings.majorityThreshold} unit="votes" weight="secondary" />
        {standings.leadRequirement > 0 && (
          <div className="text-[11px] -mt-1.5" style={{ color: COLORS.text.tertiary }}>
            +{standings.leadRequirement} lead required
          </div>
        )}
      </GlanceCard>

      {standings.networthDominationActive && (
        <div style={{ padding: '8px 12px', background: '#2a1a00', border: '1px solid #6b3d00', borderRadius: 6, fontSize: 13, color: COLORS.warning }}>
          ⚡ Networth Domination — one province controls &gt;50% of kingdom networth and holds the throne
        </div>
      )}

      <label className="flex items-center gap-2 text-sm" style={{ color: COLORS.text.tertiary }}>
        Sort by
        <select
          aria-label="Sort standings by"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          style={{
            minHeight: 44,
            padding: '4px 8px',
            background: COLORS.inputBg,
            border: `1px solid ${COLORS.inputBorder}`,
            color: COLORS.text.primary,
            fontSize: 13,
          }}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
      </label>

      {sortedStandings.length === 0 ? (
        <div style={{ padding: 32, color: COLORS.text.muted, textAlign: 'center' }}>
          No provinces in this kingdom
        </div>
      ) : (
        <div className="rounded-lg border" style={{ borderColor: COLORS.border }}>
          {sortedStandings.map((row, idx) => {
            const isMe = row.provinceId === myProvinceId;
            const isMyVote = row.provinceId === standings.myVotedForProvinceId;
            const votedForName = row.votedForProvinceId
              ? standings.standings.find((s) => s.provinceId === row.votedForProvinceId)?.provinceName ?? row.votedForProvinceId
              : null;

            return (
              <div
                key={row.provinceId}
                className="px-3 py-2"
                style={{ borderTop: idx > 0 ? `1px solid ${COLORS.border}` : undefined }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span style={{ color: row.isMonarch ? COLORS.gold : COLORS.text.primary, fontWeight: row.isMonarch || isMe ? 700 : 500, fontSize: 14 }}>
                    {row.isMonarch && <span style={{ marginRight: 4 }}>♛</span>}
                    {row.provinceName}
                    {isMe && <span className="sr-only"> (you)</span>}
                  </span>
                  <span style={{ fontWeight: isMyVote ? 700 : 500, color: isMyVote ? COLORS.link : COLORS.text.secondary, fontSize: 14, flexShrink: 0 }}>
                    {row.votesReceived}
                  </span>
                </div>
                <div style={{ color: COLORS.text.tertiary, fontSize: 12, marginTop: 2 }}>
                  Weight (votes) {row.voteWeight} · {formatEnumLabel(row.honourTitle)}
                  {votedForName && <span style={{ color: COLORS.text.muted }}> → voted for {votedForName}</span>}
                </div>
                <button
                  onClick={isMyVote ? handleRetract : () => handleVote(row.provinceId)}
                  disabled={isVoting}
                  aria-label={isMyVote ? `Retract vote for ${row.provinceName}` : `Vote for ${row.provinceName}`}
                  className="w-full mt-2"
                  style={{
                    minHeight: 44,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: isVoting ? 'not-allowed' : 'pointer',
                    opacity: isVoting ? 0.5 : 1,
                    color: isMyVote ? COLORS.text.muted : COLORS.link,
                    background: 'none',
                    border: `1px solid ${isMyVote ? COLORS.text.muted : COLORS.link}`,
                    borderRadius: 4,
                  }}
                >
                  {isMyVote ? 'Retract' : 'Vote'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
